import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { Plus, Edit2, Power, Save, ChevronDown, ChevronUp, AlertTriangle, ShieldOff, Trash2, Filter } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import AppHeader from '../components/AppHeader';
import Breadcrumb from '../components/Breadcrumb';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmDialog';
import Popover from '../components/Popover';
import {
  PageContainer, PageHeader, PageTitle, PageSubtitle,
  Card, Flex, Button,
  Input, Label, FormGroup, Badge, Spinner, LoadingContainer
} from '../components/ui';
import { theme } from '../styles/theme';
import { formatCurrency } from '../utils/currency';
import api from '../services/api';

const CURRENCIES = ['BRL', 'USD', 'EUR'];

const FEATURE_KEYS = [
  'knowledgeBase', 'vision', 'audio', 'documents', 'webchat', 'voice',
  'scheduledTasks', 'overageEnabled'
];

const BREADCRUMB_STATIC = [
  { label: 'Libben Agents', icon: '\u{1F680}', path: '/sallus/dashboard' },
];

export default function SallusSettings() {
  const { t } = useTranslation('settings');
  const toast   = useToast();
  const confirm = useConfirm();

  const breadcrumbItems = [
    ...BREADCRUMB_STATIC,
    { label: t('breadcrumbSettings'), icon: '\u2699\uFE0F' }
  ];

  const [activeTab, setActiveTab] = useState('tiers');
  const [tiers, setTiers]         = useState([]);
  const [globalSettings, setGlobalSettings] = useState({ registrationOpen: true, maintenanceMode: false, maintenanceMessage: '' });
  const [savingSettings, setSavingSettings] = useState(false);
  const [loading, setLoading]     = useState(true);
  const [editingTier, setEditingTier] = useState(null); // null = fechado, {} = novo, tier = editar
  const [expandedTier, setExpandedTier] = useState(null);

  // Credit Costs state
  const [creditCosts, setCreditCosts] = useState([]);
  const [creditCostsLoading, setCreditCostsLoading] = useState(false);
  const [creditCostFilter, setCreditCostFilter] = useState('');
  const [editingCreditCost, setEditingCreditCost] = useState(null); // null = closed, {} = new, item = edit

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tiersRes, settingsRes] = await Promise.all([
        api.get('/platform/tiers'),
        api.get('/platform/settings')
      ]);
      setTiers(tiersRes.data.tiers || []);
      setGlobalSettings(settingsRes.data.settings || { registrationOpen: true, maintenanceMode: false, maintenanceMessage: '' });
    } catch (e) {
      toast.error(t('loadError'));
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  // ── Credit Costs ──
  const loadCreditCosts = useCallback(async (category) => {
    setCreditCostsLoading(true);
    try {
      const params = category ? `?category=${category}` : '';
      const res = await api.get(`/admin/credit-costs${params}`);
      setCreditCosts(res.data.items || []);
    } catch (e) {
      toast.error(t('loadError'));
    } finally {
      setCreditCostsLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab === 'creditCosts') {
      loadCreditCosts(creditCostFilter);
    }
  }, [activeTab, creditCostFilter, loadCreditCosts]);

  const handleDeleteCreditCost = async (service) => {
    const ok = await confirm({
      title: t('creditCosts.deleteConfirm', { name: service.displayName }),
      danger: true,
      confirmLabel: t('creditCosts.deleted')
    });
    if (!ok) return;
    try {
      await api.delete(`/admin/credit-costs/${service.serviceId}`);
      toast.success(t('creditCosts.deleted'));
      loadCreditCosts(creditCostFilter);
    } catch (e) {
      toast.error(t('saveError'));
    }
  };

  const handleDeactivate = async (tier) => {
    const ok = await confirm({
      title: t('tiers.deactivateConfirm', { name: tier.name }),
      danger: true,
      confirmLabel: t('tiers.deactivated')
    });
    if (!ok) return;
    try {
      await api.delete(`/platform/tiers/${tier.tierId}`);
      toast.success(t('tiers.deactivated'));
      load();
    } catch (e) {
      toast.error(t('saveError'));
    }
  };

  const handleSaveGlobalSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put('/platform/settings', globalSettings);
      toast.success(t('system.saveSuccess'));
    } catch (e) {
      toast.error(t('saveError'));
    } finally {
      setSavingSettings(false);
    }
  };

  return (
    <>
      <AppHeader title={t('title')} />
      <BreadcrumbBar><Breadcrumb items={breadcrumbItems} /></BreadcrumbBar>
      <PageContainer>
        <PageHeader>
          <div>
            <PageTitle>{t('title')}</PageTitle>
            <PageSubtitle>sallus_admin</PageSubtitle>
          </div>
        </PageHeader>

        <TabBar>
          {['tiers', 'creditCosts', 'system'].map(tab => (
            <Tab key={tab} $active={activeTab === tab} onClick={() => setActiveTab(tab)}>
              {tab === 'tiers' ? t('tabTiers') : tab === 'creditCosts' ? t('tabCreditCosts') : t('tabSystem')}
            </Tab>
          ))}
        </TabBar>

        {loading ? (
          <LoadingContainer><Spinner size="24px" /> {t('saving')}</LoadingContainer>
        ) : (
          <>
            {/* \u2500\u2500 Aba Tiers \u2500\u2500 */}
            {activeTab === 'tiers' && (
              <Section>
                <Flex justify="space-between" style={{ marginBottom: theme.spacing[5] }}>
                  <div>
                    <SectionTitle>{t('tiers.title')}</SectionTitle>
                    <PageSubtitle>{t('tiers.subtitle')}</PageSubtitle>
                  </div>
                  <Button onClick={() => setEditingTier({
                    tierId: '', name: '', order: 0, active: true, trialDays: 0, isPublic: true,
                    audience: 'business',
                    description: '',
                    prices: { BRL: { monthly: 0, annual: 0 }, USD: { monthly: 0, annual: 0 }, EUR: { monthly: 0, annual: 0 } },
                    monthlyCredits: 0,
                    extraPackCredits: 0,
                    extraPackPrice: { BRL: 0, USD: 0, EUR: 0 },
                    maxExtraPacksPerMonth: 0,
                    limits: { agents: 1, channels: 1, members: 3, scheduledTasks: 0, scheduledExecs: 0 },
                    features: { knowledgeBase: false, vision: false, audio: false, documents: false, webchat: false, voice: false, scheduledTasks: false, overageEnabled: false }
                  })}>
                    <Plus size={14} /> {t('tiers.newTier')}
                  </Button>
                </Flex>

                {editingTier && (
                  <TierForm
                    tier={editingTier}
                    t={t}
                    onSave={async (data) => {
                      try {
                        await api.post('/platform/tiers', data);
                        toast.success(t('saveSuccess'));
                        setEditingTier(null);
                        load();
                      } catch (e) {
                        const msg = e.response?.data?.message;
                        if (e.response?.status === 409) toast.error(msg || t('tiers.trialConflict'));
                        else toast.error(t('saveError'));
                      }
                    }}
                    onCancel={() => setEditingTier(null)}
                  />
                )}

                {tiers.length === 0 ? (
                  <EmptyMsg>{t('tiers.noTiers')}</EmptyMsg>
                ) : (
                  tiers.map(tier => (
                    <TierCard key={tier.tierId} $active={tier.active !== false}>
                      <TierCardHeader onClick={() => setExpandedTier(expandedTier === tier.tierId ? null : tier.tierId)}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing[1], minWidth: 0 }}>
                          <Flex gap={theme.spacing[3]}>
                            <TierName>{tier.name}</TierName>
                            <Badge variant="neutral">order: {tier.order ?? '\u2014'}</Badge>
                            {tier.audience && <Badge variant="neutral">{tier.audience}</Badge>}
                            {tier.isPublic === false && <Badge variant="danger">{t('tiers.private')}</Badge>}
                            {tier.active === false && <Badge variant="danger">{t('inactive')}</Badge>}
                          </Flex>
                          {tier.description && <TierDescription>{tier.description}</TierDescription>}
                        </div>
                        <Flex gap={theme.spacing[2]}>
                          <PriceChip>{formatCurrency(tier.prices?.BRL?.monthly || 0, 'BRL', 'pt-BR')}{t('perMonth')}</PriceChip>
                          <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); setEditingTier({ ...tier }); }}>
                            <Edit2 size={13} />
                          </Button>
                          {tier.active !== false && (
                            <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); handleDeactivate(tier); }}>
                              <Power size={13} />
                            </Button>
                          )}
                          {expandedTier === tier.tierId ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        </Flex>
                      </TierCardHeader>

                      {expandedTier === tier.tierId && (
                        <TierDetails>
                          <DetailGrid>
                            <DetailSection>
                              <DetailLabel>{t('tiers.creditFields')}</DetailLabel>
                              <DetailRow>
                                <span>{t('tiers.monthlyCredits')}</span>
                                <strong>{tier.monthlyCredits ?? 0}</strong>
                              </DetailRow>
                              <DetailRow>
                                <span>{t('tiers.extraPackCredits')}</span>
                                <strong>{tier.extraPackCredits ?? 0}</strong>
                              </DetailRow>
                              <DetailRow>
                                <span>{t('tiers.extraPackPrice')}</span>
                                <strong>
                                  {formatCurrency(tier.extraPackPrice?.BRL || 0, 'BRL', 'pt-BR')} / {formatCurrency(tier.extraPackPrice?.USD || 0, 'USD', 'en')} / {formatCurrency(tier.extraPackPrice?.EUR || 0, 'EUR', 'en')}
                                </strong>
                              </DetailRow>
                              <DetailRow>
                                <span>{t('tiers.maxExtraPacksPerMonth')}</span>
                                <strong>{tier.maxExtraPacksPerMonth ?? 0}</strong>
                              </DetailRow>
                            </DetailSection>
                            <DetailSection>
                              <DetailLabel>{t('tiers.limits')}</DetailLabel>
                              {Object.entries(tier.limits || {}).map(([k, v]) => (
                                <DetailRow key={k}>
                                  <span>{k}</span>
                                  <strong>{v === -1 ? t('tiers.unlimited') : v}</strong>
                                </DetailRow>
                              ))}
                            </DetailSection>
                            <DetailSection>
                              <DetailLabel>{t('tiers.prices')}</DetailLabel>
                              {CURRENCIES.map(cur => (
                                <DetailRow key={cur}>
                                  <span>{cur}</span>
                                  <strong>{formatCurrency(tier.prices?.[cur]?.monthly || 0, cur)}{t('perMonth')}</strong>
                                </DetailRow>
                              ))}
                            </DetailSection>
                            <DetailSection>
                              <DetailLabel>{t('tiers.features')}</DetailLabel>
                              {FEATURE_KEYS.map(k => (
                                <DetailRow key={k}>
                                  <span>{k}</span>
                                  <Badge variant={tier.features?.[k] ? 'success' : 'neutral'}>
                                    {tier.features?.[k] ? '\u2713' : '\u2717'}
                                  </Badge>
                                </DetailRow>
                              ))}
                            </DetailSection>
                          </DetailGrid>
                        </TierDetails>
                      )}
                    </TierCard>
                  ))
                )}
              </Section>
            )}

            {/* \u2500\u2500 Aba Cat\u00E1logo de Cr\u00E9ditos \u2500\u2500 */}
            {activeTab === 'creditCosts' && (
              <Section>
                <Flex justify="space-between" style={{ marginBottom: theme.spacing[5] }}>
                  <div>
                    <SectionTitle>{t('creditCosts.title')}</SectionTitle>
                    <PageSubtitle>{t('creditCosts.subtitle')}</PageSubtitle>
                  </div>
                  <Button onClick={() => setEditingCreditCost({
                    serviceId: '', displayName: '', category: 'llm',
                    creditsPerUnit: 1, unit: 'message', modelId: '', awsCostPerUnit: '',
                    awsCostInputPer1K: '', awsCostOutputPer1K: ''
                  })}>
                    <Plus size={14} /> {t('creditCosts.newService')}
                  </Button>
                </Flex>

                <FilterBar>
                  <Filter size={14} />
                  <FilterSelect
                    value={creditCostFilter}
                    onChange={e => setCreditCostFilter(e.target.value)}
                  >
                    <option value="">{t('creditCosts.allCategories')}</option>
                    <option value="llm">LLM</option>
                    <option value="transcription">Transcription</option>
                    <option value="document">Document</option>
                    <option value="rag">RAG</option>
                    <option value="vision">Vision</option>
                    <option value="connector_message">Connector Message</option>
                    <option value="campaign">Campaign</option>
                  </FilterSelect>
                </FilterBar>

                {editingCreditCost && (
                  <CreditCostForm
                    service={editingCreditCost}
                    t={t}
                    onSave={async (data) => {
                      try {
                        if (editingCreditCost._isEditing) {
                          await api.put(`/admin/credit-costs/${data.serviceId}`, data);
                        } else {
                          await api.post('/admin/credit-costs', data);
                        }
                        toast.success(t('saveSuccess'));
                        setEditingCreditCost(null);
                        loadCreditCosts(creditCostFilter);
                      } catch (e) {
                        const msg = e.response?.data?.message;
                        if (e.response?.status === 409) toast.error(msg || t('creditCosts.serviceConflict'));
                        else toast.error(t('saveError'));
                      }
                    }}
                    onCancel={() => setEditingCreditCost(null)}
                  />
                )}

                {creditCostsLoading ? (
                  <LoadingContainer><Spinner size="24px" /></LoadingContainer>
                ) : creditCosts.length === 0 ? (
                  <EmptyMsg>{t('creditCosts.noServices')}</EmptyMsg>
                ) : (
                  <CreditCostTable>
                    <thead>
                      <tr>
                        <th>{t('creditCosts.displayName')}</th>
                        <th>{t('creditCosts.category')}</th>
                        <th>{t('creditCosts.creditsPerUnit')}</th>
                        <th>{t('creditCosts.unit')}</th>
                        <th>{t('creditCosts.modelId')}</th>
                        <th>Input/1K</th>
                        <th>Output/1K</th>
                        <th>{t('creditCosts.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {creditCosts.map(svc => (
                        <tr key={svc.serviceId}>
                          <td><ServiceName>{svc.displayName}</ServiceName><ServiceId>{svc.serviceId}</ServiceId></td>
                          <td><Badge variant="neutral">{svc.category}</Badge></td>
                          <td><strong>{svc.creditsPerUnit}</strong></td>
                          <td>{svc.unit}</td>
                          <td><ModelIdCell title={svc.modelId || ''}>{svc.modelId ? svc.modelId.substring(0, 24) + (svc.modelId.length > 24 ? '\u2026' : '') : '\u2014'}</ModelIdCell></td>
                          <td>{svc.awsCostInputPer1K != null ? `$${svc.awsCostInputPer1K}` : '\u2014'}</td>
                          <td>{svc.awsCostOutputPer1K != null ? `$${svc.awsCostOutputPer1K}` : '\u2014'}</td>
                          <td>
                            <Flex gap={theme.spacing[1]}>
                              <Button variant="ghost" size="sm" onClick={() => setEditingCreditCost({ ...svc, _isEditing: true })}>
                                <Edit2 size={13} />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteCreditCost(svc)}>
                                <Trash2 size={13} />
                              </Button>
                            </Flex>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </CreditCostTable>
                )}
              </Section>
            )}

            {/* \u2500\u2500 Aba Sistema \u2500\u2500 */}
            {activeTab === 'system' && (
              <Section>
                <SectionTitle>{t('system.title')}</SectionTitle>
                <PageSubtitle style={{ marginBottom: theme.spacing[5] }}>{t('system.subtitle')}</PageSubtitle>

                <SettingsCard $danger={globalSettings.maintenanceMode}>
                  <SettingsRow>
                    <SettingsInfo>
                      <SettingsLabel><ShieldOff size={15} /> {t('system.maintenanceMode')}</SettingsLabel>
                      <SettingsDesc>
                        {t('system.maintenanceModeDesc')}
                      </SettingsDesc>
                    </SettingsInfo>
                    <Toggle
                      $active={globalSettings.maintenanceMode}
                      onClick={() => setGlobalSettings(s => ({ ...s, maintenanceMode: !s.maintenanceMode }))}
                    >
                      {globalSettings.maintenanceMode ? t('system.toggleActive') : t('system.toggleInactive')}
                    </Toggle>
                  </SettingsRow>
                  {globalSettings.maintenanceMode && (
                    <MaintenanceMsgBox>
                      <label style={{ fontSize: theme.typography.sm, fontWeight: 500, color: theme.colors.textPrimary }}>
                        {t('system.maintenanceMsgLabel')}
                      </label>
                      <textarea
                        rows={3}
                        value={globalSettings.maintenanceMessage}
                        onChange={e => setGlobalSettings(s => ({ ...s, maintenanceMessage: e.target.value }))}
                        placeholder={t('system.maintenanceMsgPlaceholder')}
                        style={{ width: '100%', padding: theme.spacing[3], border: `1px solid ${theme.colors.border}`, borderRadius: theme.radii.md, fontFamily: 'inherit', fontSize: theme.typography.sm, resize: 'vertical', outline: 'none', marginTop: theme.spacing[2] }}
                      />
                    </MaintenanceMsgBox>
                  )}
                </SettingsCard>

                <SettingsCard $danger={!globalSettings.registrationOpen}>
                  <SettingsRow>
                    <SettingsInfo>
                      <SettingsLabel><AlertTriangle size={15} /> {t('system.registrations')}</SettingsLabel>
                      <SettingsDesc>
                        {t('system.registrationsDesc')}
                      </SettingsDesc>
                    </SettingsInfo>
                    <Toggle
                      $active={globalSettings.registrationOpen}
                      onClick={() => setGlobalSettings(s => ({ ...s, registrationOpen: !s.registrationOpen }))}
                    >
                      {globalSettings.registrationOpen ? t('system.toggleOpen') : t('system.toggleBlocked')}
                    </Toggle>
                  </SettingsRow>
                </SettingsCard>

                <Button onClick={handleSaveGlobalSettings} disabled={savingSettings} style={{ marginTop: theme.spacing[2] }}>
                  <Save size={14} /> {savingSettings ? t('saving') : t('system.saveSettings')}
                </Button>
              </Section>
            )}
          </>
        )}
      </PageContainer>
    </>
  );
}

// ── TierForm ──────────────────────────────────────────────────────────────────

function TierForm({ tier, t, onSave, onCancel }) {
  const [data, setData] = useState({ ...tier });
  const [saving, setSaving] = useState(false);

  const set = (path, value) => {
    setData(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const parts = path.split('.');
      let obj = next;
      for (let i = 0; i < parts.length - 1; i++) {
        if (!obj[parts[i]]) obj[parts[i]] = {};
        obj = obj[parts[i]];
      }
      obj[parts[parts.length - 1]] = value;
      return next;
    });
  };

  const handleSave = async () => {
    if (!data.tierId || !data.name) return;
    setSaving(true);
    try { await onSave(data); }
    finally { setSaving(false); }
  };

  return (
    <FormCard>
      <FormCardTitle>{data.tierId ? t('tiers.editTier') : t('tiers.newTier')}</FormCardTitle>

      <FormGrid>
        <FormGroup>
          <LabelWithHelp>
            <Label>{t('tiers.tierId')}</Label>
            <Popover content={t('tiers.tierIdHelp')}><HelpIcon>?</HelpIcon></Popover>
          </LabelWithHelp>
          <Input value={data.tierId} onChange={e => set('tierId', e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))} placeholder="ex: starter" disabled={!!tier.tierId} />
        </FormGroup>
        <FormGroup>
          <Label>{t('tiers.name')}</Label>
          <Input value={data.name} onChange={e => set('name', e.target.value)} placeholder="ex: Starter" />
        </FormGroup>
        <FormGroup>
          <LabelWithHelp>
            <Label>{t('tiers.order')}</Label>
            <Popover content={t('tiers.orderHelp')}><HelpIcon>?</HelpIcon></Popover>
          </LabelWithHelp>
          <Input type="number" value={data.order ?? 0} onChange={e => set('order', parseInt(e.target.value))} />
        </FormGroup>
        <FormGroup>
          <LabelWithHelp>
            <Label>{t('tiers.trialDays')}</Label>
            <Popover content={t('tooltips.trialDays')}><HelpIcon>?</HelpIcon></Popover>
          </LabelWithHelp>
          <Input type="number" min="0" value={data.trialDays ?? 0} onChange={e => set('trialDays', parseInt(e.target.value) || 0)} />
        </FormGroup>
      </FormGrid>

      <FormGroup style={{ marginTop: theme.spacing[4] }}>
        <LabelWithHelp>
          <Label>{t('tiers.description')}</Label>
          <Popover content={t('tiers.descriptionHelp')}><HelpIcon>?</HelpIcon></Popover>
        </LabelWithHelp>
        <DescriptionTextarea
          rows={2}
          maxLength={160}
          value={data.description || ''}
          onChange={e => set('description', e.target.value)}
          placeholder={t('tiers.descriptionPlaceholder')}
        />
      </FormGroup>

      <ToggleRow>
        <ToggleItem>
          <input type="checkbox" checked={data.active !== false} onChange={e => set('active', e.target.checked)} />
          <span>{t('tiers.active')}</span>
        </ToggleItem>

        <ToggleItem>
          <input type="checkbox" checked={data.isPublic !== false} onChange={e => set('isPublic', e.target.checked)} />
          <span>{t('tiers.isPublic')}</span>
          <Popover content={t('tooltips.isPublic')}><HelpIcon>?</HelpIcon></Popover>
        </ToggleItem>
      </ToggleRow>

      <FormGroup style={{ maxWidth: 240 }}>
        <Label>{t('tiers.audience')}</Label>
        <select
          value={data.audience || 'business'}
          onChange={e => set('audience', e.target.value)}
          style={{ width: '100%', padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 14 }}
        >
          <option value="personal">{t('tiers.audiencePersonal')}</option>
          <option value="business">{t('tiers.audienceBusiness')}</option>
        </select>
      </FormGroup>

      {/* Pre\u00E7os por moeda */}
      <SubTitle>{t('tiers.prices')}</SubTitle>
      <PriceGrid>
        {CURRENCIES.map(cur => (
          <PriceCurrencyBlock key={cur}>
            <CurrencyLabel>{cur}</CurrencyLabel>
            <FormGroup>
              <Label>{t('tiers.priceMonthly')}</Label>
              <Input type="number" min="0" value={data.prices?.[cur]?.monthly ?? 0}
                onChange={e => set(`prices.${cur}.monthly`, Number(e.target.value))} />
            </FormGroup>
            <FormGroup>
              <Label>{t('tiers.priceAnnual')}</Label>
              <Input type="number" min="0" value={data.prices?.[cur]?.annual ?? 0}
                onChange={e => set(`prices.${cur}.annual`, Number(e.target.value))} />
            </FormGroup>
          </PriceCurrencyBlock>
        ))}
      </PriceGrid>

      {/* Cr\u00E9ditos */}
      <SubTitle>{t('tiers.creditFields')}</SubTitle>
      <FormGrid>
        <FormGroup>
          <Label>{t('tiers.monthlyCredits')}</Label>
          <Input type="number" min="0" value={data.monthlyCredits ?? 0}
            onChange={e => set('monthlyCredits', Number(e.target.value))} />
        </FormGroup>
        <FormGroup>
          <Label>{t('tiers.extraPackCredits')}</Label>
          <Input type="number" min="0" value={data.extraPackCredits ?? 0}
            onChange={e => set('extraPackCredits', Number(e.target.value))} />
        </FormGroup>
        <FormGroup>
          <Label>{t('tiers.maxExtraPacksPerMonth')}</Label>
          <Input type="number" min="0" value={data.maxExtraPacksPerMonth ?? 0}
            onChange={e => set('maxExtraPacksPerMonth', Number(e.target.value))} />
        </FormGroup>
      </FormGrid>
      <PriceGrid style={{ marginTop: theme.spacing[3] }}>
        {CURRENCIES.map(cur => (
          <PriceCurrencyBlock key={cur}>
            <CurrencyLabel>{cur}</CurrencyLabel>
            <FormGroup>
              <Label>{t('tiers.extraPackPrice')}</Label>
              <Input type="number" min="0" step="0.01" value={data.extraPackPrice?.[cur] ?? 0}
                onChange={e => set(`extraPackPrice.${cur}`, Number(e.target.value))} />
            </FormGroup>
          </PriceCurrencyBlock>
        ))}
      </PriceGrid>

      {/* Limites */}
      <SubTitle>{t('tiers.limits')}</SubTitle>
      <FormGrid>
        {[
          ['agents', t('tiers.agents')],
          ['channels', t('tiers.channels')],
          ['members', t('tiers.members')],
          ['scheduledTasks', t('tiers.scheduledTasks')],
          ['scheduledExecs', t('tiers.scheduledExecs')],
        ].map(([key, label]) => (
          <FormGroup key={key}>
            <Label>{label}</Label>
            <Input type="number" min="-1" value={data.limits?.[key] ?? 0}
              onChange={e => {
                const raw = e.target.value;
                if (raw === '' || raw === '-') { set(`limits.${key}`, raw); return; }
                const n = parseInt(raw);
                if (!isNaN(n)) set(`limits.${key}`, n);
              }} />
          </FormGroup>
        ))}
      </FormGrid>

      {/* Features */}
      <SubTitle>{t('tiers.features')}</SubTitle>
      <FeaturesGrid>
        {FEATURE_KEYS.map(key => (
          <ToggleItem key={key}>
            <input type="checkbox" checked={!!data.features?.[key]}
              onChange={e => set(`features.${key}`, e.target.checked)} />
            <span>{t(`tiers.feature${key.charAt(0).toUpperCase() + key.slice(1)}`)}</span>
          </ToggleItem>
        ))}
      </FeaturesGrid>

      <Flex gap={theme.spacing[3]} style={{ marginTop: theme.spacing[5] }}>
        <Button variant="secondary" onClick={onCancel}>{t('cancel', { ns: 'common' })}</Button>
        <Button variant="primary" onClick={handleSave} disabled={saving || !data.tierId || !data.name}>
          {saving ? t('saving') : t('save', { ns: 'common' })}
        </Button>
      </Flex>
    </FormCard>
  );
}

// ── CreditCostForm ────────────────────────────────────────────────────────────

const CREDIT_CATEGORIES = ['llm', 'transcription', 'document', 'rag', 'vision', 'connector_message', 'campaign'];
const CREDIT_UNITS = ['message', 'page', 'minute', 'query', 'image', 'template_message'];

function CreditCostForm({ service, t, onSave, onCancel }) {
  const [data, setData] = useState({ ...service });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!data.serviceId || !data.displayName || !data.creditsPerUnit) return;
    setSaving(true);
    const payload = {
      serviceId: data.serviceId,
      displayName: data.displayName,
      category: data.category,
      creditsPerUnit: Number(data.creditsPerUnit),
      unit: data.unit,
      modelId: data.modelId || null,
      awsCostPerUnit: data.awsCostPerUnit ? Number(data.awsCostPerUnit) : null,
      awsCostInputPer1K: data.awsCostInputPer1K ? Number(data.awsCostInputPer1K) : null,
      awsCostOutputPer1K: data.awsCostOutputPer1K ? Number(data.awsCostOutputPer1K) : null,
    };
    try { await onSave(payload); }
    finally { setSaving(false); }
  };

  return (
    <FormCard>
      <FormCardTitle>{service._isEditing ? t('creditCosts.editService') : t('creditCosts.newService')}</FormCardTitle>
      <FormGrid>
        <FormGroup>
          <Label>{t('creditCosts.serviceId')}</Label>
          <Input
            value={data.serviceId}
            onChange={e => setData(d => ({ ...d, serviceId: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '') }))}
            placeholder="ex: bedrock_claude_haiku_4_5"
            disabled={!!service._isEditing}
          />
        </FormGroup>
        <FormGroup>
          <Label>{t('creditCosts.displayName')}</Label>
          <Input
            value={data.displayName}
            onChange={e => setData(d => ({ ...d, displayName: e.target.value }))}
            placeholder="ex: Claude Haiku 4.5"
          />
        </FormGroup>
        <FormGroup>
          <Label>{t('creditCosts.category')}</Label>
          <FilterSelect
            value={data.category}
            onChange={e => setData(d => ({ ...d, category: e.target.value }))}
            style={{ width: '100%' }}
          >
            {CREDIT_CATEGORIES.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </FilterSelect>
        </FormGroup>
        <FormGroup>
          <Label>{t('creditCosts.creditsPerUnit')}</Label>
          <Input
            type="number"
            min="1"
            value={data.creditsPerUnit}
            onChange={e => setData(d => ({ ...d, creditsPerUnit: e.target.value }))}
          />
        </FormGroup>
        <FormGroup>
          <Label>{t('creditCosts.unit')}</Label>
          <FilterSelect
            value={data.unit}
            onChange={e => setData(d => ({ ...d, unit: e.target.value }))}
            style={{ width: '100%' }}
          >
            {CREDIT_UNITS.map(u => (
              <option key={u} value={u}>{u}</option>
            ))}
          </FilterSelect>
        </FormGroup>
        <FormGroup>
          <Label>{t('creditCosts.modelId')}</Label>
          <Input
            value={data.modelId || ''}
            onChange={e => setData(d => ({ ...d, modelId: e.target.value }))}
            placeholder="ex: us.anthropic.claude-haiku-4-5-20251001-v1:0"
          />
        </FormGroup>
        <FormGroup>
          <Label>{t('creditCosts.awsCostPerUnit')}</Label>
          <Input
            type="number"
            min="0"
            step="0.0001"
            value={data.awsCostPerUnit || ''}
            onChange={e => setData(d => ({ ...d, awsCostPerUnit: e.target.value }))}
            placeholder={t('creditCosts.awsCostPlaceholder')}
          />
        </FormGroup>
        <FormGroup>
          <Label>{t('creditCosts.awsCostInputPer1K')}</Label>
          <Input
            type="number"
            min="0"
            step="0.000001"
            value={data.awsCostInputPer1K || ''}
            onChange={e => setData(d => ({ ...d, awsCostInputPer1K: e.target.value }))}
            placeholder="ex: 0.0008"
          />
        </FormGroup>
        <FormGroup>
          <Label>{t('creditCosts.awsCostOutputPer1K')}</Label>
          <Input
            type="number"
            min="0"
            step="0.000001"
            value={data.awsCostOutputPer1K || ''}
            onChange={e => setData(d => ({ ...d, awsCostOutputPer1K: e.target.value }))}
            placeholder="ex: 0.004"
          />
        </FormGroup>
      </FormGrid>
      <Flex gap={theme.spacing[3]} style={{ marginTop: theme.spacing[5] }}>
        <Button variant="secondary" onClick={onCancel}>{t('cancel', { ns: 'common' })}</Button>
        <Button variant="primary" onClick={handleSave} disabled={saving || !data.serviceId || !data.displayName || !data.creditsPerUnit}>
          <Save size={14} /> {saving ? t('saving') : t('save', { ns: 'common' })}
        </Button>
      </Flex>
    </FormCard>
  );
}

// ── Styled ────────────────────────────────────────────────────────────────────

const BreadcrumbBar = styled.div`background: ${theme.colors.white}; padding: 0 ${theme.spacing[6]}; border-bottom: 1px solid ${theme.colors.border};`;
const TabBar = styled.div`display: flex; gap: 0; border-bottom: 2px solid ${theme.colors.border}; margin-bottom: ${theme.spacing[6]};`;
const Tab = styled.button`padding: ${theme.spacing[3]} ${theme.spacing[5]}; background: none; border: none; border-bottom: 3px solid ${p => p.$active ? theme.colors.primary : 'transparent'}; color: ${p => p.$active ? theme.colors.primary : theme.colors.textMuted}; font-size: ${theme.typography.sm}; font-weight: ${theme.typography.medium}; cursor: pointer; margin-bottom: -2px; transition: ${theme.transitions.fast}; &:hover { color: ${theme.colors.primary}; }`;
const Section = styled.div``;
const SectionTitle = styled.h2`font-size: ${theme.typography.lg}; font-weight: ${theme.typography.semibold}; color: ${theme.colors.textPrimary}; margin: 0 0 ${theme.spacing[1]} 0;`;
const EmptyMsg = styled.p`color: ${theme.colors.textMuted}; font-size: ${theme.typography.sm}; text-align: center; padding: ${theme.spacing[8]};`;

const TierCard = styled(Card)`margin-bottom: ${theme.spacing[3]}; opacity: ${p => p.$active ? 1 : 0.5}; padding: 0; overflow: hidden;`;
const TierCardHeader = styled.div`display: flex; align-items: center; justify-content: space-between; padding: ${theme.spacing[4]} ${theme.spacing[5]}; cursor: pointer; &:hover { background: ${theme.colors.gray50}; }`;
const TierName = styled.span`font-size: ${theme.typography.base}; font-weight: ${theme.typography.semibold}; color: ${theme.colors.textPrimary};`;
const TierDescription = styled.span`font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 420px;`;
const PriceChip = styled.span`font-size: ${theme.typography.sm}; font-weight: ${theme.typography.semibold}; color: ${theme.colors.primary}; background: ${theme.colors.primaryLight}; padding: 2px ${theme.spacing[2]}; border-radius: ${theme.radii.full};`;
const TierDetails = styled.div`padding: ${theme.spacing[5]}; border-top: 1px solid ${theme.colors.border}; background: ${theme.colors.gray50};`;
const DetailGrid = styled.div`display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: ${theme.spacing[5]};`;
const DetailSection = styled.div`display: flex; flex-direction: column; gap: ${theme.spacing[2]};`;
const DetailLabel = styled.div`font-size: ${theme.typography.xs}; font-weight: ${theme.typography.semibold}; color: ${theme.colors.textMuted}; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: ${theme.spacing[1]};`;
const DetailRow = styled.div`display: flex; justify-content: space-between; font-size: ${theme.typography.xs}; color: ${theme.colors.textSecondary};`;

const FormCard = styled(Card)`margin-bottom: ${theme.spacing[5]}; border-left: 3px solid ${theme.colors.primary};`;
const FormCardTitle = styled.h3`font-size: ${theme.typography.base}; font-weight: ${theme.typography.semibold}; color: ${theme.colors.textPrimary}; margin: 0 0 ${theme.spacing[5]} 0;`;
const FormGrid = styled.div`display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: ${theme.spacing[4]};`;
const SubTitle = styled.h4`font-size: ${theme.typography.sm}; font-weight: ${theme.typography.semibold}; color: ${theme.colors.textSecondary}; text-transform: uppercase; letter-spacing: 0.05em; margin: ${theme.spacing[5]} 0 ${theme.spacing[3]} 0; border-top: 1px solid ${theme.colors.border}; padding-top: ${theme.spacing[4]};`;
const PriceGrid = styled.div`display: grid; grid-template-columns: repeat(3, 1fr); gap: ${theme.spacing[4]};`;
const PriceCurrencyBlock = styled.div`display: flex; flex-direction: column; gap: ${theme.spacing[3]}; padding: ${theme.spacing[3]}; background: ${theme.colors.gray50}; border-radius: ${theme.radii.md};`;
const CurrencyLabel = styled.div`font-size: ${theme.typography.sm}; font-weight: ${theme.typography.bold}; color: ${theme.colors.primary};`;
const ToggleRow = styled.div`display: flex; gap: ${theme.spacing[6]}; margin: ${theme.spacing[3]} 0;`;
const ToggleItem = styled.label`display: flex; align-items: center; gap: ${theme.spacing[2]}; font-size: ${theme.typography.sm}; color: ${theme.colors.textPrimary}; cursor: pointer;`;
const FeaturesGrid = styled.div`display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: ${theme.spacing[3]};`;
const LabelWithHelp = styled.div`display: flex; align-items: center; gap: ${theme.spacing[1]}; margin-bottom: ${theme.spacing[1]};`;
const HelpIcon = styled.span`width: 14px; height: 14px; border-radius: 50%; background: ${theme.colors.gray300}; color: white; font-size: 9px; font-weight: 700; display: inline-flex; align-items: center; justify-content: center; cursor: help;`;
const DescriptionTextarea = styled.textarea`width: 100%; padding: ${theme.spacing[2]} ${theme.spacing[3]}; border: 1px solid ${theme.colors.border}; border-radius: ${theme.radii.md}; font-size: ${theme.typography.sm}; font-family: inherit; color: ${theme.colors.textPrimary}; resize: vertical; &:focus { outline: none; border-color: ${theme.colors.primary}; }`;

const FilterBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  margin-bottom: ${theme.spacing[4]};
  color: ${theme.colors.textMuted};
`;
const FilterSelect = styled.select`
  padding: 6px 10px;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.sm};
  font-size: ${theme.typography.sm};
  color: ${theme.colors.textPrimary};
  background: ${theme.colors.white};
  outline: none;
  &:focus { border-color: ${theme.colors.primary}; }
`;
const CreditCostTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${theme.typography.sm};
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
  overflow: hidden;
  th {
    text-align: left;
    padding: ${theme.spacing[3]} ${theme.spacing[4]};
    background: ${theme.colors.gray50};
    color: ${theme.colors.textMuted};
    font-weight: ${theme.typography.semibold};
    font-size: ${theme.typography.xs};
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border-bottom: 1px solid ${theme.colors.border};
  }
  td {
    padding: ${theme.spacing[3]} ${theme.spacing[4]};
    border-bottom: 1px solid ${theme.colors.border};
    vertical-align: middle;
  }
  tbody tr:last-child td { border-bottom: none; }
  tbody tr:hover { background: ${theme.colors.gray50}; }
`;
const ServiceName = styled.div`
  font-weight: ${theme.typography.medium};
  color: ${theme.colors.textPrimary};
`;
const ServiceId = styled.div`
  font-size: ${theme.typography.xs};
  color: ${theme.colors.textMuted};
  font-family: ${theme.typography.fontMono};
`;
const ModelIdCell = styled.span`
  font-size: ${theme.typography.xs};
  font-family: ${theme.typography.fontMono};
  color: ${theme.colors.textSecondary};
`;

const SettingsCard = styled(Card)`
  margin-bottom: ${theme.spacing[4]};
  border-left: 3px solid ${p => p.$danger ? theme.colors.danger : theme.colors.border};
  background: ${p => p.$danger ? theme.colors.dangerLight : theme.colors.white};
`;
const SettingsRow = styled.div`display: flex; align-items: flex-start; justify-content: space-between; gap: ${theme.spacing[4]};`;
const SettingsInfo = styled.div`flex: 1;`;
const SettingsLabel = styled.div`display: flex; align-items: center; gap: ${theme.spacing[2]}; font-size: ${theme.typography.base}; font-weight: ${theme.typography.semibold}; color: ${theme.colors.textPrimary}; margin-bottom: ${theme.spacing[1]};`;
const SettingsDesc = styled.div`font-size: ${theme.typography.sm}; color: ${theme.colors.textSecondary}; line-height: 1.5; code { background: ${theme.colors.gray100}; padding: 1px 4px; border-radius: 3px; font-size: 11px; }`;
const Toggle = styled.button`
  flex-shrink: 0; padding: ${theme.spacing[2]} ${theme.spacing[4]};
  border: none; border-radius: ${theme.radii.full}; cursor: pointer;
  font-size: ${theme.typography.xs}; font-weight: ${theme.typography.bold}; letter-spacing: 0.05em;
  background: ${p => p.$active ? theme.colors.success : theme.colors.gray200};
  color: ${p => p.$active ? 'white' : theme.colors.textMuted};
  transition: ${theme.transitions.fast};
  &:hover { opacity: 0.85; }
`;
const MaintenanceMsgBox = styled.div`margin-top: ${theme.spacing[4]}; padding-top: ${theme.spacing[4]}; border-top: 1px solid ${theme.colors.dangerBorder};`;
